import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { incidentService } from "@/services/incidentService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

export interface IncidentRef {
  id: string;
  name: string;
  status?: string;
}

interface IncidentsInputProps {
  /** Array of incident objects */
  value: IncidentRef[];
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (incidents: IncidentRef[]) => void;
}

export function IncidentsInput({
  value,
  state = "editing",
  label = "Linked Incidents",
  error,
  onChange,
}: IncidentsInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  const handleChipPress = useCallback(
    (incidentId: string) => {
      const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
      const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
      if (currentPanel && currentId) {
        pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
      }
      navigateToEntity("incident", incidentId);
    },
    [currentParams, pushNavigation, currentEntity, navigateToEntity],
  );

  // Fetch all incident options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const incidents = await incidentService.getIncidents();
        if (cancelled) return;
        setOptions(
          incidents.map((i: any) => ({
            id: i.id!,
            name: i.name || "Untitled Incident",
            description: i.status || undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load incidents:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items from value
  const chipItems = useMemo<ChipItem[]>(() => {
    return value.map((incident) => ({
      id: incident.id,
      name: incident.name,
      readonly: false,
    }));
  }, [value]);

  const handleAdd = useCallback(
    (incidentId: string) => {
      if (value.some((i) => i.id === incidentId)) return;
      const opt = options.find((o) => o.id === incidentId);
      if (!opt) return;
      onChange?.([
        ...value,
        {
          id: opt.id,
          name: opt.name,
          status: opt.description,
        },
      ]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (incidentId: string) => {
      onChange?.(value.filter((i) => i.id !== incidentId));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="alert-circle-outline"
      accentTone="red"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search incidents..."
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onChipPress={handleChipPress}
    />
  );
}
